import { useState } from 'react';
import { supabase } from '@cleanpro/common-ui';
import { QUOTE_STATUS, QuoteStatus, Quote } from '@cleanpro/common-ui';

/**
 * Custom hook for managing quote actions (status updates, broadcasting).
 * 
 * Features:
 * - Handles all quote status transitions
 * - Automatically sets appropriate timestamps
 * - Optimistic UI updates
 * - Proper error handling
 * - Broadcasting to contractors
 * 
 * @param quote - The current quote object
 * @param onUpdate - Callback to update the local quote state optimistically
 * @returns Object containing action functions and state
 * 
 * @example
 * ```tsx
 * const { updateStatus, broadcast, updating, error } = useQuoteActions(quote, setQuote);
 * 
 * <button onClick={() => updateStatus(QUOTE_STATUS.AWAITING_RESPONSE)} disabled={updating}>
 *   Send Quote
 * </button>
 * ```
 */
export function useQuoteActions(
  quote: Quote | null,
  onUpdate: (updates: Partial<Quote>) => void
) {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Updates the quote status with proper timestamp handling.
   * 
   * @param status - The new status to set
   * @throws Error if the update fails
   */
  const updateStatus = async (status: QuoteStatus) => {
    if (!quote) {
      throw new Error('No quote available to update');
    }
    
    setUpdating(true);
    setError(null);
    
    try {
      const now = new Date().toISOString();
      const updateData: Record<string, string> = { 
        status,
        updated_at: now
      };

      // Set appropriate timestamp based on status transition
      const timestampMap: Partial<Record<QuoteStatus, keyof Quote>> = {
        [QUOTE_STATUS.AWAITING_RESPONSE]: 'sentAt',
        [QUOTE_STATUS.APPROVED]: 'acceptedAt',
        [QUOTE_STATUS.CHANGES_REQUESTED]: 'respondedAt',
        [QUOTE_STATUS.ARCHIVED]: 'cancelledAt'
      };

      const timestampField = timestampMap[status];
      if (timestampField && !quote[timestampField]) {
        // Convert camelCase to snake_case for database
        const dbField = timestampField.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
        updateData[dbField] = now;
        
        // Set responded_at for terminal states (when customer or system responds)
        const terminalStatuses = [QUOTE_STATUS.APPROVED, QUOTE_STATUS.CHANGES_REQUESTED, QUOTE_STATUS.ARCHIVED] as const;
        if (terminalStatuses.some(s => s === status) && !quote.respondedAt) {
          updateData.responded_at = now;
        }
      }

      // Update in database
      const { error: updateError } = await supabase
        .from('quotes')
        .update(updateData)
        .eq('id', quote.id);

      if (updateError) throw updateError;

      // Optimistic update - apply changes immediately to local state
      const optimisticUpdates: Partial<Quote> = {
        status,
        updatedAt: now
      };

      if (updateData.sent_at) optimisticUpdates.sentAt = now;
      if (updateData.accepted_at) optimisticUpdates.acceptedAt = now;
      if (updateData.cancelled_at) optimisticUpdates.cancelledAt = now;
      if (updateData.responded_at) optimisticUpdates.respondedAt = now;

      onUpdate(optimisticUpdates);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update quote status';
      setError(message);
      console.error('Failed to update quote status:', err);
      throw err;
    } finally {
      setUpdating(false);
    }
  };

  /**
   * Broadcasts the quote to selected contractors.
   * 
   * @param contractorIds - Array of contractor business IDs to broadcast to
   * @throws Error if the broadcast fails or database migration is needed
   */
  const broadcast = async (contractorIds: string[]) => {
    if (!quote) {
      throw new Error('No quote available to broadcast');
    }

    // Prevent broadcasting if quote is still in exclusive period
    if (quote.isExclusive && quote.status === QUOTE_STATUS.EXCLUSIVE_PENDING) {
      throw new Error('Cannot broadcast quote while exclusive period is active. Wait for expiration or decline.');
    }

    if (contractorIds.length === 0) {
      throw new Error('Please select at least one contractor');
    }

    setUpdating(true);
    setError(null);

    try {
      // Insert broadcast records for each selected contractor
      const broadcasts = contractorIds.map(businessId => ({
        quote_id: quote.id,
        business_id: businessId,
        status: 'pending'
      }));

      const { error: broadcastError } = await supabase
        .from('quote_broadcasts')
        .insert(broadcasts);

      if (broadcastError) {
        // Check for specific schema/migration errors
        if (broadcastError.code === 'PGRST204' || broadcastError.message.includes('schema cache')) {
          throw new Error(
            'Database migration required: The quote_broadcasts table needs to be created. ' +
            'Please run the migration in supabase/migrations/20250101000002_add_quote_broadcasts.sql. ' +
            'See MIGRATION_INSTRUCTIONS.md for details.'
          );
        }
        throw broadcastError;
      }

      console.log('✓ Broadcast records inserted successfully, now updating quote status...');

      // Update quote status to 'pending_assignment' since it's been broadcast to contractors
      // Also ensure exclusive flag is cleared
      const now = new Date().toISOString();
      console.log('Attempting to update quote status:', {
        quoteId: quote.id,
        currentStatus: quote.status,
        newStatus: QUOTE_STATUS.PENDING_ASSIGNMENT,
        isExclusive: quote.isExclusive
      });

      const { data: updateData, error: updateError, count } = await supabase
        .from('quotes')
        .update({
          status: QUOTE_STATUS.PENDING_ASSIGNMENT,
          is_exclusive: false,
          updated_at: now,
        })
        .eq('id', quote.id)
        .select();

      console.log('Update response:', { data: updateData, error: updateError, count });

      if (updateError) {
        console.error('❌ Failed to update quote status after broadcast:', updateError);
        throw new Error(`Database update failed: ${updateError.message}`);
      }

      if (!updateData || updateData.length === 0) {
        console.error('❌ Update returned no rows - quote may not exist or RLS policy blocking update');
        throw new Error('Quote update succeeded but no rows were affected. This may indicate an RLS policy issue.');
      }

      console.log('✓ Quote status updated successfully to pending_assignment');
        
      // Update local quote state using the onUpdate callback
      onUpdate({ 
        status: QUOTE_STATUS.PENDING_ASSIGNMENT, 
        isExclusive: false,
        updatedAt: now
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to broadcast quote to contractors';
      setError(message);
      console.error('Error broadcasting quote:', err);
      throw err;
    } finally {
      setUpdating(false);
    }
  };

  return { 
    updateStatus, 
    broadcast, 
    updating, 
    error,
    clearError: () => setError(null)
  };
}
